import { useCallback, useMemo } from "react";
import {
  EnrichedSupplierForDelivery,
  ManualDeliveryOptionEntry,
  SelectedDeliveryOption,
} from "../../types";
import { MANUAL_DELIVERY_OPTION_RADIO_VALUE } from "../../utils/deliveryOptionSelectionUtils";
import {
  isSystemGeneratedDeliveryOption,
  TNG_SUPPLIER_ID,
} from "../../utils/isVbzCustomerTngSplitCase";

interface UseEstimateSplitSelectionParams {
  supplier: EnrichedSupplierForDelivery;
  selection: SelectedDeliveryOption | undefined;
  onSelectionChange: (selection: SelectedDeliveryOption | undefined) => void;
  isVbzCustomerTngSplitCase?: boolean;
}

export function useEstimateSplitSelection({
  supplier,
  selection,
  onSelectionChange,
  isVbzCustomerTngSplitCase = false,
}: UseEstimateSplitSelectionParams) {
  const selectedDeliveryOptionId =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "option"
      ? selection.deliveryDetails.selectedDeliveryOptionId
      : undefined;

  const isManualSelected =
    selection?.customerAction === "confirm" &&
    selection?.deliveryDetails?.selectionType === "manual";

  const isBuyTimeSelected = selection?.customerAction === "buytime";

  const radioValue = useMemo(() => {
    if (isManualSelected) return MANUAL_DELIVERY_OPTION_RADIO_VALUE;
    if (selectedDeliveryOptionId) return selectedDeliveryOptionId;
    return "";
  }, [isManualSelected, selectedDeliveryOptionId]);

  const defaultSupplierId = useMemo(() => {
    const splitSupplier = supplier.supplierOptions?.find((opt) => !opt.isAssociated);
    if (splitSupplier) return splitSupplier.supplierId;
    return supplier.supplierOptions?.[0]?.supplierId || supplier.supplierId || undefined;
  }, [supplier.supplierOptions, supplier.supplierId]);

  const handleDeliveryOptionSelect = useCallback(
    (deliveryOptionId: string) => {
      const targetOption = supplier.deliveryOptions.find(
        (opt) => opt._id === deliveryOptionId,
      );
      if (!targetOption) return;
      if (
        isVbzCustomerTngSplitCase &&
        isSystemGeneratedDeliveryOption(targetOption)
      ) {
        return;
      }

      const initialSupplierId = targetOption.supplierId || defaultSupplierId || undefined;

      onSelectionChange({
        supplierId: initialSupplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "option",
          selectedDeliveryOptionId: deliveryOptionId,
        },
      });
    },
    [supplier.deliveryOptions, defaultSupplierId, onSelectionChange, isVbzCustomerTngSplitCase],
  );

  const handleManualOptionSelect = useCallback(() => {
    if (isManualSelected) return;

    const initialManualSupplierId = isVbzCustomerTngSplitCase
      ? TNG_SUPPLIER_ID
      : selection?.deliveryDetails?.manualEntry?.supplierId ||
        defaultSupplierId ||
        undefined;

    onSelectionChange({
      supplierId: initialManualSupplierId,
      customerAction: "confirm",
      deliveryDetails: {
        selectionType: "manual",
        manualEntry: {
          cpt: selection?.deliveryDetails?.manualEntry?.cpt || "",
          deliveryCharge: selection?.deliveryDetails?.manualEntry?.deliveryCharge ?? 0,
          cptSlab: selection?.deliveryDetails?.manualEntry?.cptSlab,
          supplierId: initialManualSupplierId,
        },
      },
    });
  }, [isManualSelected, defaultSupplierId, selection, onSelectionChange, isVbzCustomerTngSplitCase]);

  const handleHold = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId || undefined,
      customerAction: selection?.customerAction === "hold" ? "confirm" : "hold",
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const handleCancel = useCallback(() => {
    onSelectionChange({
      supplierId: supplier.supplierId || undefined,
      customerAction:
        selection?.customerAction === "cancel" ? "confirm" : "cancel",
    });
  }, [supplier.supplierId, selection, onSelectionChange]);

  const handleManualEntryCancel = useCallback(
    (isExpired: boolean) => {
      if (isExpired) return;
      onSelectionChange(undefined);
    },
    [onSelectionChange],
  );

  const handleManualOptionUpdate = useCallback(
    (entry: ManualDeliveryOptionEntry) => {
      onSelectionChange({
        supplierId: entry.supplierId || supplier.supplierId || undefined,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "manual",
          manualEntry: entry,
        },
      });
    },
    [supplier.supplierId, onSelectionChange],
  );

  const handleOptionSupplierChange = useCallback(
    (deliveryOptionId: string, supplierId: string) => {
      onSelectionChange({
        supplierId,
        customerAction: "confirm",
        deliveryDetails: {
          selectionType: "option",
          selectedDeliveryOptionId: deliveryOptionId,
        },
      });
    },
    [onSelectionChange],
  );

  const isDeliveryOptionSelected = useCallback(
    (deliveryOptionId: string) =>
      selectedDeliveryOptionId === deliveryOptionId,
    [selectedDeliveryOptionId],
  );

  return {
    selectedDeliveryOptionId,
    isManualSelected,
    isBuyTimeSelected,
    radioValue,
    handleDeliveryOptionSelect,
    handleManualOptionSelect,
    handleHold,
    handleCancel,
    handleManualEntryCancel,
    handleManualOptionUpdate,
    handleOptionSupplierChange,
    isDeliveryOptionSelected,
  };
}
